import React from 'react';
import styled from 'styled-components';
import { WaiverRequiredFields, FieldRequirement } from '../../types/waiver';

const Container = styled.div`
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 8px;
  padding: 20px;
`;

const Title = styled.h3`
  margin: 0 0 16px 0;
  color: ${({ theme }) => theme.colors.text};
  font-size: 16px;
`;

const FieldRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 0;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};

  &:last-child {
    border-bottom: none;
  }
`;

const FieldName = styled.span`
  color: ${({ theme }) => theme.colors.text};
  font-size: 14px;
  flex: 1;
`;

const CheckboxGroup = styled.div`
  display: flex;
  gap: 24px;
`;

const CheckboxLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: 13px;
  cursor: pointer;

  input {
    width: 16px;
    height: 16px;
    cursor: pointer;
  }
`;

const FixedLabel = styled.span`
  color: ${({ theme }) => theme.colors.success};
  font-size: 13px;
  font-weight: 500;
`;

interface WaiverRequiredFieldsEditorProps {
  fields: WaiverRequiredFields;
  onChange: (fields: WaiverRequiredFields) => void;
}

const fieldLabels: Record<string, string> = {
  fullName: 'Full Name',
  address: 'Address',
  phone: 'Phone Number',
  email: 'Email',
  emergencyContact: 'Emergency Contact',
  idNumber: 'ID Number',
  idIssueDate: 'ID Issue Date',
  dateOfBirth: 'Date of Birth',
};

const WaiverRequiredFieldsEditor: React.FC<WaiverRequiredFieldsEditorProps> = ({
  fields,
  onChange,
}) => {
  const handleFieldChange = (
    fieldName: keyof WaiverRequiredFields,
    property: 'enabled' | 'required',
    value: boolean
  ) => {
    if (fieldName === 'fullName') return;

    const currentField = fields[fieldName] as FieldRequirement;
    const updatedField = { ...currentField, [property]: value };

    if (property === 'enabled' && !value) {
      updatedField.required = false;
    }

    onChange({
      ...fields,
      [fieldName]: updatedField,
    });
  };

  const renderFieldRow = (fieldName: keyof WaiverRequiredFields) => {
    if (fieldName === 'fullName') {
      return (
        <FieldRow key={fieldName}>
          <FieldName>{fieldLabels[fieldName]}</FieldName>
          <FixedLabel>Always Required</FixedLabel>
        </FieldRow>
      );
    }

    const field = fields[fieldName] as FieldRequirement;

    return (
      <FieldRow key={fieldName}>
        <FieldName>{fieldLabels[fieldName]}</FieldName>
        <CheckboxGroup>
          <CheckboxLabel>
            <input
              type="checkbox"
              checked={field.enabled}
              onChange={(e) => handleFieldChange(fieldName, 'enabled', e.target.checked)}
            />
            Enabled
          </CheckboxLabel>
          <CheckboxLabel>
            <input
              type="checkbox"
              checked={field.required}
              disabled={!field.enabled}
              onChange={(e) => handleFieldChange(fieldName, 'required', e.target.checked)}
            />
            Required
          </CheckboxLabel>
        </CheckboxGroup>
      </FieldRow>
    );
  };

  return (
    <Container>
      <Title>Form Fields Configuration</Title>
      {Object.keys(fieldLabels).map((fieldName) =>
        renderFieldRow(fieldName as keyof WaiverRequiredFields)
      )}
    </Container>
  );
};

export default WaiverRequiredFieldsEditor;
